import { useReadContracts, useBalance } from 'wagmi';
import { erc20Abi, isAddress } from 'viem';
import { useMemo } from 'react';
import { useWeb3 } from '@/contexts/web3Provider';
import { resNetworkList } from '@/component/wallet/NetworkList';

export interface TokenBalance {
  symbol: string;
  balance: string;
  decimals: number;
  isLoading: boolean;
  error: Error | null;
}

export interface UseMultiBalanceResult {
  balances: Map<string, TokenBalance>;
  isLoading: boolean;
  isAllLoaded: boolean; // 모든 조회 완료 여부
  refetch: () => void;
}

export function useMultiBalance(networks: resNetworkList[] | null): UseMultiBalanceResult {
  const { address } = useWeb3();

  // Native coin과 ERC20 토큰 분리
  const { nativeCoin, erc20Tokens } = useMemo(() => {
    if (!networks) return { nativeCoin: null, erc20Tokens: [] };

    const native = networks.find((n) => !n.token || n.token === '');
    const tokens = networks.filter((n) => n.token && n.token !== '' && isAddress(n.token));

    return { nativeCoin: native, erc20Tokens: tokens };
  }, [networks]);

  // Native coin 잔액 조회 (useBalance)
  const {
    data: nativeBalance,
    isLoading: nativeLoading,
    error: nativeError,
    refetch: refetchNative,
  } = useBalance({
    address: address as `0x${string}`,
    query: {
      enabled: !!address && !!nativeCoin,
    },
  });

  // ERC20 토큰들 잔액 일괄 조회 (useReadContracts - multicall)
  const erc20Contracts = useMemo(() => {
    if (!address || erc20Tokens.length === 0) return [];

    return erc20Tokens.flatMap((token) => [
      {
        address: token.token as `0x${string}`,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [address as `0x${string}`],
      },
      {
        address: token.token as `0x${string}`,
        abi: erc20Abi,
        functionName: 'decimals',
      },
      {
        address: token.token as `0x${string}`,
        abi: erc20Abi,
        functionName: 'symbol',
      },
    ]);
  }, [address, erc20Tokens]);

  const {
    data: erc20Data,
    isLoading: erc20Loading,
    error: erc20Error,
    refetch: refetchErc20,
  } = useReadContracts({
    contracts: erc20Contracts,
    query: {
      enabled: erc20Contracts.length > 0,
    },
  });

  // 전체 로딩 상태 계산
  const isLoading = nativeLoading || erc20Loading;

  // 모든 조회 완료 여부 (Native coin 조회 완료 AND ERC20 조회 완료)
  const isAllLoaded = useMemo(() => {
    const nativeReady = !nativeCoin || (!nativeLoading && nativeBalance !== undefined);
    const erc20Ready = erc20Tokens.length === 0 || (!erc20Loading && erc20Data !== undefined);
    return nativeReady && erc20Ready;
  }, [nativeCoin, nativeLoading, nativeBalance, erc20Tokens.length, erc20Loading, erc20Data]);

  // 결과를 Map으로 정리 (모든 조회가 완료된 후에만 데이터 반환)
  const balances = useMemo(() => {
    const result = new Map<string, TokenBalance>();

    // 모든 조회가 완료되지 않았으면 빈 Map 반환
    if (!isAllLoaded) {
      return result;
    }

    // Native coin 잔액 추가
    if (nativeCoin) {
      result.set(nativeCoin.symbol, {
        symbol: nativeBalance?.symbol || nativeCoin.symbol,
        balance: nativeBalance?.value?.toString() || '0',
        decimals: nativeBalance?.decimals || 18,
        isLoading: false,
        error: nativeError,
      });
    }

    // ERC20 토큰 잔액 추가
    if (erc20Data && erc20Tokens.length > 0) {
      erc20Tokens.forEach((token, index) => {
        const baseIndex = index * 3;
        const balanceResult = erc20Data[baseIndex];
        const decimalsResult = erc20Data[baseIndex + 1];
        const symbolResult = erc20Data[baseIndex + 2];

        const balance = balanceResult?.status === 'success' ? String(balanceResult.result) : '0';
        const decimals = decimalsResult?.status === 'success' ? Number(decimalsResult.result) : 18;
        const symbol = symbolResult?.status === 'success' ? String(symbolResult.result) : token.symbol;

        result.set(token.symbol, {
          symbol,
          balance,
          decimals,
          isLoading: false,
          error: erc20Error,
        });
      });
    }

    return result;
  }, [isAllLoaded, nativeCoin, nativeBalance, nativeError, erc20Data, erc20Tokens, erc20Error]);

  const refetch = () => {
    refetchNative();
    refetchErc20();
  };

  return {
    balances,
    isLoading,
    isAllLoaded,
    refetch,
  };
}
